/**
 * manage/sections — the policy-shaped view, rendered.
 *
 * THE ONE RULE IN THIS FILE: render what `view` CONTAINS and omit what it does
 * not. Every card below returns `null` when its member is absent, so a tenant
 * that hides staff, prices, questions or the customer's own data gets a page
 * with no holes in it rather than a page of "—". There is no settings logic
 * here, no placeholder for an absent field, and no re-derivation of anything the
 * server nulled: a `null` is the answer, not a gap.
 *
 * Nothing in this file decides authority either. A masked value is rendered
 * masked — the page never holds the unmasked one — and the affordance that
 * offers to change that is the step-up in `dialogs.tsx`.
 */
import type { JSX, ReactNode } from "react";
import { StatusPill } from "@v0y4ge/jrni-ui";
import type { ManageQuestionDto, ManageViewDto } from "@v0y4ge/api-contracts";
import type { ManageIntl, ManageStringKey } from "./strings";
import type { TimeFormatter } from "./format";
import { showsViewerTime, viewerTimeZone } from "./format";

import { useT } from "@v0y4ge/i18n-runtime";
export interface RenderContext {
  view: ManageViewDto;
  intl: ManageIntl;
  fmt: TimeFormatter;
}

/* ── atoms ───────────────────────────────────────────────────────────── */

export function Card({ title, children }: { title?: string; children: ReactNode }): JSX.Element {
  return (
    <section className="mng-card">
      {title ? <h2 className="mng-card-title">{title}</h2> : null}
      {children}
    </section>
  );
}

function Rows({ children }: { children: ReactNode }): JSX.Element {
  return <dl className="mng-rows">{children}</dl>;
}

function Row({ label, children }: { label: string; children: ReactNode }): JSX.Element {
  return (
    <>
      <dt>{label}</dt>
      <dd>{children}</dd>
    </>
  );
}

/**
 * Sanitized-on-the-server rich text (`branding.footer_html`, and an
 * `information` answer). The contract states both are run through the platform's
 * allowlist sanitizer before they reach any client — "never raw tenant HTML" —
 * so this renders them rather than dropping authored content. It is the ONLY
 * place this page writes HTML it did not compose, and it is one call site so
 * the assumption is auditable.
 */
function ServerHtml({ html }: { html: string }): JSX.Element {
  return <div className="mng-rich" dangerouslySetInnerHTML={{ __html: html }} />;
}

/* ── the booking's own name ──────────────────────────────────────────── */

/**
 * What this booking IS, from whichever member the journey type populates. No
 * fallback string: a view with none of them renders the status alone rather than
 * a fictional title.
 */
export function bookingTitle(view: ManageViewDto): string | null {
  if (view.event) return view.event.title;
  const service = view.services?.[0]?.name;
  if (service) return service;
  if (view.queue) return view.queue.location_name;
  return null;
}

const STATUS_TONE: Record<string, "success" | "warning" | "danger" | "neutral" | "info"> = {
  confirmed: "success",
  checked_in: "success",
  completed: "neutral",
  rescheduled: "info",
  pending: "warning",
  unconfirmed: "warning",
  payment_pending: "warning",
  waitlisted: "info",
  cancelled: "danger",
  no_show: "danger",
};

export function StatusBadge({ view, intl }: { view: ManageViewDto; intl: ManageIntl }): JSX.Element {
  return (
    <StatusPill tone={STATUS_TONE[view.status] ?? "neutral"} dot>
      {intl.t(`status.${view.status}` as ManageStringKey)}
    </StatusPill>
  );
}

/* ── when ────────────────────────────────────────────────────────────── */

export function WhenLine({ view, intl, fmt }: RenderContext): JSX.Element {
  const t = useT();
  const { schedule } = view;
  const zone = schedule.timezone;
  const minutes = fmt.minutesBetween(schedule.start_utc, schedule.end_utc);
  return (
    <div className="mng-stack">
      <p className="mng-when">
        {fmt.full(schedule.start_utc, zone)}
        {" – "}
        {fmt.time(schedule.end_utc, zone)}
        {minutes > 0 ? t("manage.sections.text", "· {arg1}", { arg1: intl.t("when.duration", { n: intl.number(minutes) }) }) : ""}
      </p>
      {showsViewerTime(schedule) ? (
        <p className="mng-sub">
          {intl.t("when.local", {
            t: `${fmt.full(schedule.start_utc, viewerTimeZone())}`,
          })}
        </p>
      ) : null}
      {schedule.previous_start_utc ? (
        <p className="mng-sub">
          {intl.t("when.moved", { t: fmt.full(schedule.previous_start_utc, zone) })}
        </p>
      ) : null}
    </div>
  );
}

/* ── where ───────────────────────────────────────────────────────────── */

export function WhereCard({ view, intl, fmt }: RenderContext): JSX.Element | null {
  const location = view.location;
  // `hidden` is the policy's own member of the union — the card disappears
  // entirely rather than rendering an empty "Where".
  if (!location || location.kind === "hidden") return null;
  const title = intl.t("section.where");

  if (location.kind === "branch") {
    return (
      <Card title={title}>
        <div className="mng-stack">
          <p className="mng-when">{location.name}</p>
          <p className="mng-sub">{location.address}</p>
          <div className="mng-inline">
            {location.phone ? <a href={`tel:${location.phone}`}>{location.phone}</a> : null}
            {location.email ? <a href={`mailto:${location.email}`}>{location.email}</a> : null}
            {location.map ? (
              <a
                href={`https://www.openstreetmap.org/?mlat=${location.map.lat}&mlon=${location.map.lng}#map=17/${location.map.lat}/${location.map.lng}`}
                rel="noopener noreferrer"
                target="_blank"
              >
                {intl.t("where.map")}
              </a>
            ) : null}
          </div>
        </div>
      </Card>
    );
  }

  if (location.kind === "freetext") {
    return (
      <Card title={title}>
        <p className="mng-when">{location.label}</p>
      </Card>
    );
  }

  if (location.kind === "customer_address") {
    return (
      <Card title={title}>
        <p className="mng-when">{location.address}</p>
      </Card>
    );
  }

  if (location.kind === "phone") {
    const number = location.number;
    const copy = location.direction === "we_call_you"
      ? number ? intl.t("where.callYou", { number }) : intl.t("where.callYouNoNumber")
      : number ? intl.t("where.callUs", { number }) : intl.t("where.callUsNoNumber");
    return (
      <Card title={title}>
        <p className="mng-when">{copy}</p>
      </Card>
    );
  }

  // virtual — the join URL is `null` until the configured reveal time; the
  // server never ships it early, so there is nothing to hide client-side.
  /* The meeting's own state wins when the view carries it (CC 0136). Its `note` is a
     reason code for staff and is never shown here. */
  const meeting = (view as { meeting?: { status?: string; join_url?: string | null } | null }).meeting ?? null;
  if (meeting && meeting.status !== "ready") {
    return (
      <Card title={title}>
        <div className="mng-stack">
          <p className="mng-when">{location.provider}</p>
          <p className="mng-sub">{meeting.status === "pending" ? "Your meeting link is being prepared." : "Your advisor will send you the meeting link separately."}</p>
        </div>
      </Card>
    );
  }
  const joinUrl = meeting ? (meeting.join_url ?? location.join_url) : location.join_url;
  return (
    <Card title={title}>
      <div className="mng-stack">
        <p className="mng-when">{location.provider}</p>
        {joinUrl ? (
          <a href={joinUrl} rel="noopener noreferrer" target="_blank">
            {intl.t("where.join")}
          </a>
        ) : location.reveals_at ? (
          <p className="mng-sub">
            {intl.t("where.joinLater", { t: fmt.full(location.reveals_at, view.schedule.timezone) })}
          </p>
        ) : null}
        {location.passcode ? (
          <p className="mng-sub">{intl.t("where.passcode", { code: location.passcode })}</p>
        ) : null}
      </div>
    </Card>
  );
}

/* ── who ─────────────────────────────────────────────────────────────── */

export function WhoCard({ view, intl }: RenderContext): JSX.Element | null {
  const staff = view.staff;
  if ((!staff || staff.length === 0) && !view.staff_pending) return null;
  return (
    <Card title={intl.t("section.who")}>
      {staff && staff.length > 0 ? (
        <div className="mng-stack">
          {staff.map((member) => (
            <div className="mng-stack" key={member.name}>
              <p className="mng-when">{member.name}</p>
              {member.job_title ? <p className="mng-sub">{member.job_title}</p> : null}
              {member.description ? <p className="mng-sub">{member.description}</p> : null}
            </div>
          ))}
        </div>
      ) : (
        <p className="mng-sub">{intl.t("who.pending")}</p>
      )}
    </Card>
  );
}

/* ── services ────────────────────────────────────────────────────────── */

export function ServicesCard({ view, intl, fmt }: RenderContext): JSX.Element | null {
  const t = useT();
  const services = view.services;
  // One service is already the page title; a second one earns a card.
  if (!services || services.length < 2) return null;
  return (
    <Card>
      <Rows>
        {services.map((service) => (
          <Row
            key={service.name}
            label={service.name}
          >
            {intl.t("when.duration", { n: intl.number(service.duration_minutes) })}
            {service.price !== null
              ? " · " + (service.price_is_from ? "≥ " : "") + fmt.money(service.price, service.currency)
              : ""}
          </Row>
        ))}
      </Rows>
    </Card>
  );
}

/* ── payment ─────────────────────────────────────────────────────────── */

export function PaymentCard({ view, intl, fmt }: RenderContext): JSX.Element | null {
  const payment = view.payment;
  if (!payment) return null;
  const amount = payment.charged_price ?? payment.price;
  return (
    <Card title={intl.t("section.payment")}>
      <div className="mng-stack">
        <div className="mng-inline">
          {amount !== null ? (
            <span className="mng-when">{fmt.money(amount, payment.currency)}</span>
          ) : null}
          <StatusPill tone={payment.paid_status === "paid" ? "success" : "warning"}>
            {intl.t(`payment.${payment.paid_status}` as ManageStringKey)}
          </StatusPill>
        </div>
        {payment.coupon ? (
          <p className="mng-sub">
            {intl.t("payment.coupon", {
              code: payment.coupon.code,
              amount: fmt.money(payment.coupon.reduction_amount, payment.currency),
            })}
          </p>
        ) : null}
        {payment.receipt_url ? (
          <a href={payment.receipt_url} rel="noopener noreferrer" target="_blank">
            {intl.t("payment.receipt")}
          </a>
        ) : null}
      </div>
    </Card>
  );
}

/* ── the visitor's own data ──────────────────────────────────────────── */

export function YouCard({
  view,
  intl,
  onReveal,
}: RenderContext & { onReveal: (() => void) | null }): JSX.Element | null {
  const t = useT();
  const customer = view.customer;
  // `show_customer_data: false` nulls every contact member and leaves the
  // display name; a card with nothing but a masked name is not worth a section.
  const hasContact = Boolean(customer.email || customer.phone || customer.mobile || customer.birthday);
  if (!hasContact && !view.masked) return null;
  return (
    <Card title={intl.t("section.you")}>
      <Rows>
        <Row label={intl.t("section.you")}>
          {customer.salutation ? t("manage.sections.text3", "{salutation}", { salutation: customer.salutation }) : ""}
          {customer.display_name}
        </Row>
        {customer.email ? <Row label={intl.t("verify.email")}>{customer.email}</Row> : null}
        {customer.mobile ? <Row label={intl.t("verify.sms")}>{customer.mobile}</Row> : null}
        {customer.phone && customer.phone !== customer.mobile ? (
          <Row label={intl.t("where.callUsNoNumber")}>{customer.phone}</Row>
        ) : null}
      </Rows>
      {view.masked ? (
        <div className="mng-stack">
          <p className="mng-note">{intl.t("you.masked")}</p>
          {onReveal ? (
            <button className="mng-linkish" onClick={onReveal} type="button">
              {intl.t("you.reveal")}
            </button>
          ) : null}
        </div>
      ) : null}
    </Card>
  );
}

/* ── answers ─────────────────────────────────────────────────────────── */

export function answerText(
  question: ManageQuestionDto,
  intl: ManageIntl,
  fmt: TimeFormatter,
  timeZone: string,
): ReactNode {
  const answer = question.rendered_answer;
  switch (answer.kind) {
    case "text":
      return answer.value;
    case "choice":
      // A list joined by the LOCALE's list conjunction, not by ", ".
      try {
        return new Intl.ListFormat(intl.locale, { style: "long", type: "conjunction" })
          .format(answer.values);
      } catch {
        return answer.values.join(" · ");
      }
    case "number":
      return intl.number(answer.value);
    case "date":
      return fmt.date(answer.iso, timeZone);
    case "file":
      return question.file
        ? question.file.scan_state === "clean"
          ? question.file.name
          : question.file.scan_state === "pending"
            ? intl.t("answers.file.scanning")
            : intl.t("answers.file.blocked")
        : null;
    case "information":
      return <ServerHtml html={answer.html} />;
    case "masked":
      return <span className="mng-masked">{intl.t("answers.masked")}</span>;
    default:
      return null;
  }
}

/** The same answer as PLAIN TEXT, for the AUTHORED page (2026-09-09, CD docket
 *  §3): the canvas's `answers` rows are `{label, value}` strings, so a ReactNode
 *  cannot travel. Two kinds are deliberately not text — `information` is a block
 *  of sanitized tenant copy rather than an answer and returns null (the caller
 *  drops the row), and `masked` returns the masked LABEL, never a value, because
 *  the page never holds the unmasked one. */
export function answerString(
  question: ManageQuestionDto,
  intl: ManageIntl,
  fmt: TimeFormatter,
  timeZone: string,
): string | null {
  const answer = question.rendered_answer;
  switch (answer.kind) {
    case "text":
      return answer.value;
    case "choice":
      try {
        return new Intl.ListFormat(intl.locale, { style: "long", type: "conjunction" })
          .format(answer.values);
      } catch {
        return answer.values.join(" \u00b7 ");
      }
    case "number":
      return intl.number(answer.value);
    case "date":
      return fmt.date(answer.iso, timeZone);
    case "file":
      return question.file
        ? question.file.scan_state === "clean"
          ? question.file.name
          : question.file.scan_state === "pending"
            ? intl.t("answers.file.scanning")
            : intl.t("answers.file.blocked")
        : null;
    case "masked":
      return intl.t("answers.masked");
    case "information":
      return null;
    default:
      return null;
  }
}

export function AnswersCard({ view, intl, fmt }: RenderContext): JSX.Element | null {
  // `show_questions: false` sends an EMPTY array — no card, no empty state.
  if (view.questions.length === 0) return null;
  const ordered = [...view.questions].sort((a, b) => a.position - b.position);
  return (
    <Card title={intl.t("section.what")}>
      <div className="mng-stack">
        {ordered.map((question) => (
          <div className="mng-stack" key={question.id}>
            <span className="mng-answer-label">{question.label}</span>
            <span className="mng-answer-value">
              {answerText(question, intl, fmt, view.schedule.timezone)}
            </span>
          </div>
        ))}
      </div>
      {view.remarks ? (
        <>
          <hr className="mng-divider" />
          <p className="mng-sub">{view.remarks}</p>
        </>
      ) : null}
    </Card>
  );
}

/* ── event, queue, door code ─────────────────────────────────────────── */

export function EventCard({ view, intl }: RenderContext): JSX.Element | null {
  const t = useT();
  const event = view.event;
  if (!event) return null;
  return (
    <Card title={intl.t("section.event")}>
      <div className="mng-stack">
        <p className="mng-when">
          {intl.plural("event.seats", event.seats)}
          {event.ticket_type ? t("manage.sections.text4", "· {ticket_type}", { ticket_type: event.ticket_type }) : ""}
        </p>
        {event.seat_holder_names.length > 0 ? (
          <Rows>
            {/* Names only. The contract ships no other seat-holder data, so
                there is nothing here to accidentally reveal. */}
            <Row label={intl.t("event.holders")}>{event.seat_holder_names.join(", ")}</Row>
          </Rows>
        ) : null}
      </div>
    </Card>
  );
}

export function QueueCard({ view, intl }: RenderContext): JSX.Element | null {
  const queue = view.queue;
  if (!queue) return null;
  return (
    <Card title={intl.t("section.queue")}>
      <div className="mng-stack">
        <p className="mng-when">
          {queue.position !== null
            ? intl.t("queue.position", { n: intl.number(queue.position) })
            : queue.location_name}
        </p>
        {queue.estimated_wait_minutes !== null ? (
          <p className="mng-sub">
            {intl.t("queue.wait", { n: intl.number(queue.estimated_wait_minutes) })}
          </p>
        ) : (
          <p className="mng-sub">{intl.t("queue.pending")}</p>
        )}
      </div>
    </Card>
  );
}

export function DoorCodeCard({ view, intl, fmt }: RenderContext): JSX.Element | null {
  const key = view.digital_key;
  if (!key || !key.code) return null;
  return (
    <Card title={intl.t("section.key")}>
      <div className="mng-stack">
        <p className="mng-title">{key.code}</p>
        <p className="mng-sub">
          {fmt.full(key.valid_from, view.schedule.timezone)}
          {" – "}
          {fmt.time(key.valid_until, view.schedule.timezone)}
        </p>
      </div>
    </Card>
  );
}

/* ── footer ──────────────────────────────────────────────────────────── */

export function Footer({ view }: { view: ManageViewDto }): JSX.Element | null {
  const partner = view.partner;
  const footerHtml = view.branding.footer_html;
  if (partner.hidden && !footerHtml) return null;
  return (
    <footer className="mng-footer">
      <div className="mng-footer-inner">
        {partner.hidden ? null : (
          <>
            <span>{partner.name}</span>
            <div className="mng-inline">
              {partner.email ? <a href={`mailto:${partner.email}`}>{partner.email}</a> : null}
              {partner.phone ? <a href={`tel:${partner.phone}`}>{partner.phone}</a> : null}
              {partner.website_url ? (
                <a href={partner.website_url} rel="noopener noreferrer" target="_blank">
                  {partner.website_url.replace(/^https?:\/\//u, "")}
                </a>
              ) : null}
            </div>
          </>
        )}
        {footerHtml ? <ServerHtml html={footerHtml} /> : null}
      </div>
    </footer>
  );
}
